import {
  BufferGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Shape,
  ShapeGeometry,
  SphereGeometry,
  TorusGeometry,
} from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

import { APPEARANCE_PRESENTATION_CONFIG } from "../../config/appearance.ts";
import type { Outerwear } from "../../sim/appearance.ts";

const OUTERWEAR_IDS = [
  "wool_coat",
  "field_jacket",
  "cardigan",
  "raincoat",
] as const satisfies readonly Outerwear[];

export interface GarmentStyleGeometry {
  readonly shell: BufferGeometry;
  readonly trim: BufferGeometry;
  readonly buttons: BufferGeometry;
  readonly sleeve: BufferGeometry;
  readonly sleeveLength: number;
  readonly handOffset: Readonly<{ y: number; z: number }>;
}

export type GarmentGeometrySet = Readonly<
  Record<Outerwear, GarmentStyleGeometry>
>;

interface GarmentStyleView {
  readonly object: Group;
  readonly shell: Mesh;
  readonly trim: Mesh;
  readonly buttons: Mesh;
}

export interface GarmentLayerSet {
  readonly object: Group;
  readonly styles: Readonly<Record<Outerwear, GarmentStyleView>>;
}

interface Placement {
  readonly x?: number;
  readonly y?: number;
  readonly z?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  readonly scaleZ?: number;
  readonly rotateX?: number;
  readonly rotateY?: number;
  readonly rotateZ?: number;
}

function place(
  geometry: BufferGeometry,
  placement: Readonly<Placement> = {},
): BufferGeometry {
  geometry.scale(
    placement.scaleX ?? 1,
    placement.scaleY ?? 1,
    placement.scaleZ ?? 1,
  );
  geometry.rotateX(placement.rotateX ?? 0);
  geometry.rotateY(placement.rotateY ?? 0);
  geometry.rotateZ(placement.rotateZ ?? 0);
  geometry.translate(
    placement.x ?? 0,
    placement.y ?? 0,
    placement.z ?? 0,
  );
  return geometry;
}

function merge(
  geometries: readonly BufferGeometry[],
  label: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries], false);
  if (merged === null) {
    throw new Error("Could not merge " + label + " garment geometry");
  }
  merged.name = label;
  for (const geometry of geometries) {
    geometry.dispose();
  }
  return merged;
}

function polygon(
  points: readonly Readonly<{ x: number; y: number }>[],
): ShapeGeometry {
  const [first, ...rest] = points;
  if (first === undefined) {
    throw new Error("Garment polygon requires at least one point");
  }
  const shape = new Shape();
  shape.moveTo(first.x, first.y);
  for (const point of rest) {
    shape.lineTo(point.x, point.y);
  }
  shape.closePath();
  return new ShapeGeometry(shape);
}

function rectangle(width: number, height: number): ShapeGeometry {
  return polygon([
    { x: -width * 0.5, y: -height * 0.5 },
    { x: width * 0.5, y: -height * 0.5 },
    { x: width * 0.5, y: height * 0.5 },
    { x: -width * 0.5, y: height * 0.5 },
  ]);
}

function lapel(side: -1 | 1, narrow = false): ShapeGeometry {
  const outerX = (narrow ? 0.105 : 0.14) * side;
  const lowerX = (narrow ? 0.038 : 0.065) * side;
  return polygon([
    { x: 0.012 * side, y: 0.245 },
    { x: outerX, y: narrow ? 0.105 : 0.145 },
    { x: lowerX, y: narrow ? -0.18 : -0.105 },
    { x: 0.006 * side, y: -0.03 },
  ]);
}

function pocket(width = 0.13, height = 0.1): ShapeGeometry {
  return polygon([
    { x: -width * 0.42, y: height * 0.5 },
    { x: width * 0.42, y: height * 0.5 },
    { x: width * 0.5, y: -height * 0.5 },
    { x: -width * 0.5, y: -height * 0.5 },
  ]);
}

function buttons(
  coordinates: readonly Readonly<{ x: number; y: number; z?: number }>[],
  radius = 0.018,
): BufferGeometry {
  return merge(
    coordinates.map(({ x, y, z }) =>
      place(new SphereGeometry(radius, 5, 3), {
        x,
        y,
        z: z ?? 0.278,
      })
    ),
    "garment-buttons",
  );
}

const SLEEVE_ELBOW_BEND_RADIANS = -0.18;

function shoulderWedge(
  side: -1 | 1,
  width: number,
  innerRadius: number,
  outerRadius: number,
): BufferGeometry {
  return place(
    new CylinderGeometry(outerRadius, innerRadius, width, 6),
    {
      x: side * 0.215,
      y: 0.16,
      scaleZ: 0.82,
      rotateZ: side * -Math.PI * 0.5,
    },
  );
}

function sleeveEnd(
  length: number,
): Readonly<{ y: number; z: number }> {
  const upperLength = length * 0.54;
  const lowerLength = length - upperLength;
  return {
    y: -upperLength - lowerLength * Math.cos(SLEEVE_ELBOW_BEND_RADIANS),
    z: -lowerLength * Math.sin(SLEEVE_ELBOW_BEND_RADIANS),
  };
}

function sleeve(
  length: number,
  shoulderRadius: number,
  elbowRadius: number,
  cuffRadius: number,
): BufferGeometry {
  const upperLength = length * 0.54;
  const lowerLength = length - upperLength;
  const lower = new CylinderGeometry(
    elbowRadius,
    cuffRadius,
    lowerLength,
    7,
  );
  lower.translate(0, -lowerLength * 0.5, 0);
  lower.rotateX(SLEEVE_ELBOW_BEND_RADIANS);
  lower.translate(0, -upperLength, 0);
  const cuffLength = 0.055;
  const cuff = new CylinderGeometry(
    cuffRadius * 1.1,
    cuffRadius * 1.04,
    cuffLength,
    7,
  );
  cuff.translate(0, -lowerLength - cuffLength * 0.2, 0);
  cuff.rotateX(SLEEVE_ELBOW_BEND_RADIANS);
  cuff.translate(0, -upperLength, 0);
  return merge([
    place(new CylinderGeometry(shoulderRadius, elbowRadius, upperLength, 7), {
      y: -upperLength * 0.5,
    }),
    lower,
    cuff,
  ], "shaped-sleeve");
}

function woolCoatGeometry(): GarmentStyleGeometry {
  return {
    shell: merge([
      place(new CylinderGeometry(0.245, 0.27, 0.56, 8), {
        scaleZ: 0.9,
      }),
      place(new CylinderGeometry(0.245, 0.325, 0.4, 8), {
        y: -0.365,
        scaleZ: 0.88,
      }),
      shoulderWedge(-1, 0.2, 0.11, 0.07),
      shoulderWedge(1, 0.2, 0.11, 0.07),
    ], "wool-coat-shell"),
    trim: merge([
      place(lapel(-1), { z: 0.27 }),
      place(lapel(1), { z: 0.27 }),
      place(pocket(0.1, 0.07), { x: -0.14, y: -0.17, z: 0.272 }),
      place(pocket(0.1, 0.07), { x: 0.14, y: -0.17, z: 0.272 }),
      place(rectangle(0.3, 0.045), {
        y: -0.1,
        z: -0.278,
        rotateY: Math.PI,
      }),
      place(rectangle(0.018, 0.2), {
        y: -0.42,
        z: -0.29,
        rotateY: Math.PI,
      }),
    ], "wool-coat-trim"),
    buttons: buttons([
      { x: -0.045, y: 0.055 },
      { x: 0.045, y: 0.055 },
      { x: -0.045, y: -0.055 },
      { x: 0.045, y: -0.055 },
      { x: -0.045, y: -0.165 },
      { x: 0.045, y: -0.165 },
      { x: -0.105, y: -0.1, z: -0.292 },
      { x: 0.105, y: -0.1, z: -0.292 },
    ], 0.016),
    sleeve: sleeve(0.64, 0.098, 0.078, 0.068),
    sleeveLength: 0.64,
    handOffset: sleeveEnd(0.64),
  };
}

function fieldJacketGeometry(): GarmentStyleGeometry {
  return {
    shell: merge([
      place(new CylinderGeometry(0.255, 0.225, 0.5, 8), {
        y: 0.02,
        scaleZ: 0.92,
      }),
      place(new CylinderGeometry(0.225, 0.245, 0.1, 8), {
        y: -0.25,
        scaleZ: 0.92,
      }),
      place(new CylinderGeometry(0.165, 0.205, 0.1, 8, 1, true), {
        y: 0.31,
        scaleZ: 0.9,
      }),
      shoulderWedge(-1, 0.21, 0.125, 0.075),
      shoulderWedge(1, 0.21, 0.125, 0.075),
    ], "field-jacket-shell"),
    trim: merge([
      place(pocket(0.11, 0.085), { x: -0.14, y: -0.12, z: 0.276 }),
      place(pocket(0.11, 0.085), { x: 0.14, y: -0.12, z: 0.276 }),
      place(rectangle(0.025, 0.43), { y: -0.015, z: 0.279 }),
      place(polygon([
        { x: -0.17, y: -0.0275 },
        { x: 0.17, y: -0.0275 },
        { x: 0.145, y: 0.0275 },
        { x: -0.145, y: 0.0275 },
      ]), { y: 0.175, z: 0.274 }),
      place(polygon([
        { x: -0.17, y: -0.0275 },
        { x: 0.17, y: -0.0275 },
        { x: 0.145, y: 0.0275 },
        { x: -0.145, y: 0.0275 },
      ]), { y: 0.175, z: -0.24, rotateY: Math.PI }),
      place(rectangle(0.34, 0.025), {
        y: -0.255,
        z: -0.228,
        rotateY: Math.PI,
      }),
    ], "field-jacket-trim"),
    buttons: buttons([
      { x: 0, y: 0.1 },
      { x: 0, y: -0.015 },
      { x: 0, y: -0.13 },
    ], 0.012),
    sleeve: sleeve(0.68, 0.108, 0.086, 0.076),
    sleeveLength: 0.68,
    handOffset: sleeveEnd(0.68),
  };
}

function cardiganGeometry(): GarmentStyleGeometry {
  return {
    shell: merge([
      place(new CylinderGeometry(0.22, 0.225, 0.54, 8), {
        scaleZ: 0.88,
      }),
      place(new CylinderGeometry(0.23, 0.245, 0.08, 8), {
        y: -0.28,
        scaleZ: 0.9,
      }),
      shoulderWedge(-1, 0.18, 0.09, 0.055),
      shoulderWedge(1, 0.18, 0.09, 0.055),
    ], "cardigan-shell"),
    trim: merge([
      place(lapel(-1, true), { z: 0.25 }),
      place(lapel(1, true), { z: 0.25 }),
      place(rectangle(0.035, 0.32), {
        x: -0.035,
        y: -0.105,
        z: 0.252,
      }),
      place(rectangle(0.014, 0.42), {
        y: -0.07,
        z: -0.207,
        rotateY: Math.PI,
      }),
      place(rectangle(0.34, 0.022), {
        y: -0.282,
        z: -0.225,
        rotateY: Math.PI,
      }),
      place(rectangle(0.035, 0.32), {
        x: 0.035,
        y: -0.105,
        z: 0.252,
      }),
    ], "cardigan-trim"),
    buttons: buttons([
      { x: 0, y: -0.025 },
      { x: 0, y: -0.115 },
      { x: 0, y: -0.205 },
    ], 0.013),
    sleeve: sleeve(0.64, 0.08, 0.065, 0.055),
    sleeveLength: 0.64,
    handOffset: sleeveEnd(0.64),
  };
}

function raincoatGeometry(): GarmentStyleGeometry {
  return {
    shell: merge([
      place(new CylinderGeometry(0.25, 0.27, 0.56, 8), {
        scaleZ: 0.9,
      }),
      place(new CylinderGeometry(0.245, 0.345, 0.47, 8), {
        y: -0.395,
        scaleZ: 0.9,
      }),
      place(new CylinderGeometry(0.275, 0.31, 0.17, 8, 1, true), {
        y: 0.155,
        scaleZ: 0.92,
      }),
      place(new CylinderGeometry(0.17, 0.21, 0.105, 8, 1, true), {
        y: 0.31,
        scaleZ: 0.92,
      }),
      place(new SphereGeometry(0.19, 8, 5), {
        y: 0.3,
        z: -0.12,
        scaleX: 0.82,
        scaleY: 0.58,
        scaleZ: 0.74,
      }),
    ], "raincoat-shell"),
    trim: merge([
      place(new TorusGeometry(0.26, 0.015, 4, 8), {
        y: -0.205,
        rotateX: Math.PI * 0.5,
        scaleZ: 0.88,
      }),
      place(rectangle(0.028, 0.5), { y: -0.04, z: 0.282 }),
      place(rectangle(0.12, 0.022), {
        x: -0.135,
        y: -0.16,
        z: 0.284,
        rotateZ: -0.22,
      }),
      place(rectangle(0.12, 0.022), {
        x: 0.135,
        y: -0.16,
        z: 0.284,
        rotateZ: 0.22,
      }),
      place(polygon([
        { x: -0.19, y: 0.05 },
        { x: 0.19, y: 0.05 },
        { x: 0.15, y: -0.055 },
        { x: -0.15, y: -0.055 },
      ]), { y: 0.13, z: -0.292, rotateY: Math.PI }),
      place(rectangle(0.31, 0.025), {
        y: -0.205,
        z: -0.292,
        rotateY: Math.PI,
      }),
    ], "raincoat-trim"),
    buttons: buttons([
      { x: 0, y: 0.07 },
      { x: 0, y: -0.045 },
      { x: 0, y: -0.16 },
    ], 0.014),
    sleeve: sleeve(0.66, 0.11, 0.087, 0.073),
    sleeveLength: 0.66,
    handOffset: sleeveEnd(0.66),
  };
}

export function createGarmentGeometrySet(): GarmentGeometrySet {
  return {
    wool_coat: woolCoatGeometry(),
    field_jacket: fieldJacketGeometry(),
    cardigan: cardiganGeometry(),
    raincoat: raincoatGeometry(),
  };
}

export function createGarmentLayerSet(
  geometries: Readonly<GarmentGeometrySet>,
  outerwearMaterial: MeshStandardMaterial,
  trimMaterial: MeshStandardMaterial,
  accentMaterial: MeshStandardMaterial,
  name: string,
): GarmentLayerSet {
  const object = new Group();
  object.name = name;
  const styles = {} as Record<Outerwear, GarmentStyleView>;
  for (const outerwear of OUTERWEAR_IDS) {
    const styleGeometry = geometries[outerwear];
    const style = new Group();
    style.name = "garment:" + outerwear;
    style.visible = false;
    const shell = new Mesh(styleGeometry.shell, outerwearMaterial);
    shell.name = outerwear + ":shell";
    const trim = new Mesh(styleGeometry.trim, trimMaterial);
    trim.name = outerwear + ":trim";
    const styleButtons = new Mesh(styleGeometry.buttons, accentMaterial);
    styleButtons.name = outerwear + ":buttons";
    for (const mesh of [shell, trim, styleButtons]) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    }
    style.add(shell, trim, styleButtons);
    object.add(style);
    styles[outerwear] = {
      object: style,
      shell,
      trim,
      buttons: styleButtons,
    };
  }
  return { object, styles };
}

export function applyGarmentLayerSet(
  layers: Readonly<GarmentLayerSet>,
  outerwear: Outerwear,
  body: Readonly<{ torso: number; hip: number; depth: number }>,
): void {
  for (const style of Object.values(layers.styles)) {
    style.object.visible = false;
  }
  const selected = layers.styles[outerwear];
  const config =
    APPEARANCE_PRESENTATION_CONFIG.outerwearSilhouetteById[outerwear];
  const bodyWidth = (body.torso + body.hip) * 0.5;
  selected.object.visible = true;
  selected.object.scale.set(
    bodyWidth * config.garment.width,
    config.garment.height,
    body.depth * config.garment.depth,
  );
}

export function disposeGarmentGeometrySet(
  geometries: Readonly<GarmentGeometrySet>,
): void {
  const unique = new Set<BufferGeometry>();
  for (const style of Object.values(geometries)) {
    unique.add(style.shell);
    unique.add(style.trim);
    unique.add(style.buttons);
    unique.add(style.sleeve);
  }
  for (const geometry of unique) {
    geometry.dispose();
  }
}
